import type { Reference } from '@apollo/client';
import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import type { Op } from 'quill/core';
import Quill from 'quill/core';
import React, { useEffect, useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import SVGIcon from '../../components/SVGIcon';
import { isPrecedingCharAllowed } from '../../editor/PrefixListener';
import { useQuill, withQuillContextProvider } from '../../editor/QuillContext';
import { CREATION_ID } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { UserType } from '../../graphql/generated/graphql';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import mentionIcon from '../../icons/non-prefilled/16x16/mention.svg';
import textIcon from '../../icons/non-prefilled/16x16/text.svg';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import {
  MagneticWallItemActionContainer,
  MagneticWallItemButtonGroup,
  MagneticWallItemEmojiPickerButton,
  MagneticWallItemIconButton,
  MagneticWallItemSecondaryButton,
  MagneticWallItemSubmitButton,
  MagneticWallItemWithAvatar,
} from '../MagneticWallItem';
import showFullPageSheet from '../comment/showFullPageSheet';
import getCurrentMarkForComment from '../comment/utils/getCurrentMarkForComment';
import isContentEmpty from '../utils/isContentEmpty';
import styles from './AnnotationCreate.module.scss';
import AnnotationIcon, { AnnotationIconContainer } from './AnnotationIcon';
import AnnotationInput from './AnnotationInput';

const Parchment = Quill.import('parchment');

const ANNOTATION_CREATE_ANNOTATION_FRAGMENT = gql(`
  fragment AnnotationCreate_annotation on PublicAnnotationFields {
    id
    content
    link
    linkText
  }
`);

const UPDATE_ANNOTATION = gql(`
  mutation updateAnnotation($id: ID!, $content: Json, $link: String, $linkText: String) {
    updateAnnotation(id: $id, content: $content, link: $link, linkText: $linkText) {
      id
      content
      link
      linkText
    }
  }
`);

type AnnotationFragment = DocumentType<
  typeof ANNOTATION_CREATE_ANNOTATION_FRAGMENT
>;

const CREATE_ANNOTATION = gql(`
  mutation createAnnotation(
    $checksum: Int!
    $content: Json!
    $link: String
    $mark: Json!
    $postId: ID!
    $version: Int!
    $linkText: String
  ) {
    createAnnotation(
      checksum: $checksum
      content: $content
      mark: $mark
      link: $link
      postId: $postId
      version: $version
      linkText: $linkText
    ) {
      ...AnnotationCreate_annotation
    }
  }
`);

const insertText = (quill: Quill, text: string) => {
  const { index, length } = quill.getSelection(true);
  quill.insertText(index + length, text, Quill.sources.USER);
  quill.setSelection(index + length + text.length, 0, Quill.sources.USER);
};

interface AnnotationInputGroupProps {
  children: React.ReactNode;
}

const AnnotationInputGroup = ({ children }: AnnotationInputGroupProps) => (
  <div className={styles.annotationInputGroup}>{children}</div>
);

interface AnnotationInputGroupInputProps {
  value: string;
  onChange(value: string): void;
  icon: string;
  placeholder: string;
  ariaLabel: string;
}

const AnnotationInputGroupInput = ({
  value,
  onChange,
  icon,
  placeholder,
  ariaLabel,
}: AnnotationInputGroupInputProps) => {
  return (
    <div className={styles.annotationInputContainer}>
      <SVGIcon
        className={classNames(styles.icon, {
          [styles.active]: value.trim() !== '',
        })}
        size={16}
      >
        {icon}
      </SVGIcon>
      <input
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
    </div>
  );
};

interface AnnotationCreateProps {
  annotation: AnnotationFragment;
  initialContent: Op[];
  postId: string;
  onChange(ops: Op[]): void;
  onSaved(annotation: AnnotationFragment): void;
  link: string;
  onChangeLink: React.Dispatch<React.SetStateAction<string>>;
  linkText: string;
  onChangeLinkText: React.Dispatch<React.SetStateAction<string>>;
  onCancel: () => void;
}

const AnnotationCreate = ({
  annotation,
  onSaved,
  onChange,
  initialContent,
  postId,
  link,
  onChangeLink,
  linkText,
  onChangeLinkText,
  onCancel,
}: AnnotationCreateProps) => {
  const isNewAnnotation = annotation.id === CREATION_ID;
  const { quill, ancestorQuill } = useQuill();
  const [content, setContent] = useState<Op[]>(initialContent);
  const sync = useAppSelector((state) => state.editor.sync);
  const currentUser = useAppSelector((state) => state.session.user);
  const readOnly = useAppSelector((state) => state.editor.readOnly);

  const sanitizedLink = sanitizeAnnotationLink(link);
  const [hasAddedLink, setHasAddedLink] = useState(false);

  useEffect(() => {
    if (sanitizedLink) {
      setHasAddedLink(true);
    }
  }, [sanitizedLink]);

  const [createAnnotation, { loading: isCreatingAnnotation }] = useMutation(
    CREATE_ANNOTATION,
    {
      update: (proxy, { data }) => {
        if (data?.createAnnotation) {
          proxy.modify({
            id: proxy.identify({ __typename: 'Post', id: postId }),
            fields: {
              annotations: (
                existingAnnotations: readonly Reference[],
                { readField },
              ) => {
                const newAnnotationRef = proxy.writeFragment({
                  data: data.createAnnotation,
                  fragment: ANNOTATION_CREATE_ANNOTATION_FRAGMENT,
                });

                if (
                  newAnnotationRef == null ||
                  existingAnnotations.some(
                    (ref) => readField('id', ref) === data.createAnnotation.id,
                  )
                ) {
                  return [...existingAnnotations];
                }

                return [newAnnotationRef, ...existingAnnotations];
              },
            },
          });
        }
      },
    },
  );

  const [updateAnnotation, { loading: isUpdatingAnnotation }] =
    useMutation(UPDATE_ANNOTATION);

  const isSaving = isCreatingAnnotation || isUpdatingAnnotation;

  const handleSave = async () => {
    if (isNewAnnotation) {
      if (!sync) throw new Error('sync not inited');
      if (!ancestorQuill) throw new Error('quill not inited');

      const { data } = await createAnnotation({
        variables: {
          content: JSON.stringify(content),
          mark: JSON.stringify(getCurrentMarkForComment(ancestorQuill)),
          postId,
          link: sanitizedLink,
          linkText,
          version: sync.version,
          checksum: sync.checksum,
        },
      });

      if (data?.createAnnotation) {
        onSaved(data.createAnnotation);
      }
    } else {
      const { data } = await updateAnnotation({
        variables: {
          id: annotation.id,
          content: JSON.stringify(content),
          link: sanitizedLink,
          linkText,
        },
      });

      if (data?.updateAnnotation) {
        onSaved(data.updateAnnotation);
      }
    }
  };

  const handleChange = (newContent: Op[]) => {
    setContent(newContent);
    onChange(newContent);
  };

  const hasAutoFocus =
    (!showFullPageSheet() || isNewAnnotation) &&
    (readOnly || !quill?.hasFocus());

  const mentionUser = () => {
    if (!quill) return;
    const { index, length } = quill.getSelection(true);
    const targetIndex = index + length;
    const [leaf, offset] = quill.getLeaf(targetIndex);

    if (leaf instanceof Parchment.TextBlot) {
      const char = leaf.value()[offset - 1];
      if (!isPrecedingCharAllowed(char)) {
        insertText(quill, ' @');
        return;
      }
    }
    insertText(quill, '@');
  };

  return (
    <div className={styles.container}>
      <MagneticWallItemWithAvatar
        avatar={
          <AnnotationIconContainer>
            <AnnotationIcon />
          </AnnotationIconContainer>
        }
      >
        <AnnotationInput
          onChange={handleChange}
          autoFocus={hasAutoFocus}
          initialContent={initialContent}
          quill={quill}
          onSave={handleSave}
        />
      </MagneticWallItemWithAvatar>
      <AnnotationInputGroup>
        <AnnotationInputGroupInput
          value={link}
          onChange={onChangeLink}
          icon={linkIcon}
          placeholder="Paste a reference link"
          ariaLabel="Reference link"
        />
        {hasAddedLink && (
          <AnnotationInputGroupInput
            value={linkText}
            onChange={onChangeLinkText}
            icon={textIcon}
            placeholder="Add title for reference link"
            ariaLabel="Title for reference link"
          />
        )}
      </AnnotationInputGroup>
      <MagneticWallItemActionContainer>
        <MagneticWallItemEmojiPickerButton
          onPick={(emoji) => {
            if (quill) {
              insertText(quill, emoji);
            }
          }}
        />
        {currentUser.type === UserType.Visitor ? null : (
          <MagneticWallItemIconButton
            icon={mentionIcon}
            onClick={() => mentionUser()}
            tooltip="Mention someone"
          />
        )}

        <MagneticWallItemButtonGroup>
          {!isNewAnnotation && (
            <MagneticWallItemSecondaryButton onClick={onCancel}>
              Cancel
            </MagneticWallItemSecondaryButton>
          )}
          <MagneticWallItemSubmitButton
            disabled={isSaving || (isContentEmpty(content) && !sanitizedLink)}
            onClick={handleSave}
          >
            {isNewAnnotation ? 'Annotate' : 'Save'}
          </MagneticWallItemSubmitButton>
        </MagneticWallItemButtonGroup>
      </MagneticWallItemActionContainer>
    </div>
  );
};

export default withQuillContextProvider(AnnotationCreate);
